import React from 'react';
import './app-create-project.css';
import { NotificationManager } from 'react-notifications';
import { Form, Formik } from 'formik';
import * as Yup from "yup";
import services from '../../services';
import { ServiceProjects } from '../../services/service-projects';
import appEnum from '../../util/appEnum';
import BtGrid from '../bt-grid/bt-grid';
import BtRow from '../bt-row/bt-row';
import BtColumn from '../bt-column/bt-column';
import Field from '../../components/shared/Field/Field';
import Selector from '../../components/shared/Selector/Selector';
import UserSelector from '../../components/shared/UserSelector/UserSelector';
import { withTranslation } from 'react-i18next';
import { CustomAvatar } from '../../components/shared';
import { TrashIcon } from '../../components/svgs';
import { ServiceUser } from '../../services/service-user';
import { autosave } from '../../util';
import appState from '../../state/AppStateContainer';
import { components } from 'react-select';
import { ServiceNextProject } from '../../services/ServiceNextProject';
import Loading from '../../components/shared/Loading';
import NextEntityPlacementSelector from '../../components/shared/NextEntityPlacementSelector/NextEntityPlacementSelector';
import ServiceGoogleMaps from '../../services/service-google-maps';
import { debounce } from 'lodash';

const projectSchema = Yup.object().shape({
  name: Yup.string().trim().required('Obligatoriskt'),
  number: Yup.string(),
  customer: Yup.string(),
  province: Yup.object().shape({
    label: Yup.string().required('Obligatoriskt'),
    value: Yup.string().required('Obligatoriskt'),
  }),
  entreprenad: Yup.object().shape({
    label: Yup.string().required('Obligatoriskt'),
    value: Yup.string().required('Obligatoriskt'),
  }),
  estimatedCost: Yup.object().shape({
    label: Yup.string(),
    value: Yup.string(),
  }),
  projectType: Yup.object().shape({
    label: Yup.string(),
    value: Yup.string(),
  }),
  phase: Yup.object().shape({
    label: Yup.string(),
    value: Yup.string(),
  })
});

class AppCreateProject extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      name: '',
      number: '',
      responsible: '',
      customer: '',
      nextCustomerId: null,
      province: {},
      address: null,
      addressInput: '',
      addressOptions: [],
      entreprenad: {},
      estimatedCost: {},
      projectType: {},
      phase: { label: '', value: '' },
      user: {name: '', avatarUrl: ''},
      loaderShow: false,
      dropDownCities: [],
      projectTypes: [],
      dropDownProjectTypes: [],
      entreprenadOptions: [...appEnum.getEntreprenadDataSource(props.t)],
      projectCostOptions: [...appEnum.getProjectCostDataSource()],
      parentProject: null,
      isClientValid: false,
    };
    // One Places session token per typing session; renewed after each details
    // call so the autocomplete requests stay inside a billed-as-free session.
    this.addressSessionToken = ServiceGoogleMaps.newSessionToken();
    // Monotonic counters guarding against out-of-order responses.
    this.addressRequestSeq = 0;
    this.addressDetailsSeq = 0;
    // In-flight Places details lookup, awaited by submitForm.
    this.addressDetailsPromise = null;
    this.debouncedFetchAddresses = debounce(this.fetchAddressSuggestions, 400);
  }

  fetchAddressSuggestions = async (input) => {
    if (!input || input.trim().length < 3) {
      this.setState({ addressOptions: [] });
      return;
    }
    const seq = ++this.addressRequestSeq;
    const suggestions = await ServiceGoogleMaps.autocomplete(input.trim(), this.addressSessionToken);
    // A newer keystroke already fired; discard this stale response.
    if (seq !== this.addressRequestSeq) return;
    this.setState({
      addressOptions: suggestions.map(s => ({ label: s.description, value: s.placeId })),
    });
  }

  handleAddressInputChange = (input, meta) => {
    // react-select also fires this on menu open/close with an empty value;
    // ignoring those keeps us from clearing the list behind the user's back.
    if (meta?.action !== 'input-change') return;
    this.setState({ addressInput: input });
    this.debouncedFetchAddresses(input);
  }

  /**
   * Resolves the picked suggestion into the stored address shape and, when the
   * Kommun field is still empty, fills it from the resolved municipality.
   *
   * Formik owns the value: this must never setState `address`, because `address`
   * feeds `initialValues` and the form runs with `enableReinitialize`, so a
   * changed `initialValues` makes Formik resetForm and discard everything the
   * user has typed. State is synced from `values` on submit instead.
   */
  handleAddressChange = (option, setFieldValue, currentProvince) => {
    // Any new selection supersedes a lookup still in flight for the old one.
    this.addressDetailsPromise = null;

    if (!option) {
      setFieldValue('address', null);
      this.setState({ addressOptions: [] });
      return undefined;
    }

    // Free text the user typed rather than a Google suggestion.
    if (option.__isNew__ || !option.value) {
      setFieldValue('address', { formatted: option.label });
      this.setState({ addressOptions: [] });
      return undefined;
    }

    const fallback = { formatted: option.label, placeId: option.value };
    setFieldValue('address', fallback);

    // Guards against two selections resolving out of order, the same way
    // fetchAddressSuggestions guards its own responses.
    const seq = ++this.addressDetailsSeq;

    // Kept on the instance so submitForm can await it. Saving while this is in
    // flight would otherwise persist only {formatted, placeId} and lose lat/lng
    // and the components for good, since reopening the project never
    // re-resolves an address that is already selected.
    this.addressDetailsPromise = (async () => {
      try {
        const resolved = await ServiceGoogleMaps.placeDetails(option.value, this.addressSessionToken);
        if (seq !== this.addressDetailsSeq) return undefined;

        // The session ended with that details call, so start a fresh one. Only
        // the latest selection may rotate the token, or an in-flight lookup
        // would be billed outside the session its autocomplete requests
        // belonged to.
        this.addressSessionToken = ServiceGoogleMaps.newSessionToken();
        if (!resolved) return fallback;

        const address = { ...fallback, ...resolved };
        setFieldValue('address', address);

        if (!currentProvince?.value) {
          const match = this.matchKommun(address.municipality) || this.matchKommun(address.city);
          if (match) setFieldValue('province', { label: match.value, value: match.value }, true);
        }
        return address;
      } catch (error) {
        // Never reject: this promise is only awaited at submit time, and an
        // unresolved address must not block saving the project.
        console.error('address lookup failed', error);
        return fallback;
      }
    })();

    return this.addressDetailsPromise;
  }

  /**
   * Google returns the municipality as "Stockholm" or "Stockholms kommun"
   * depending on the address, while the City collection stores the bare name.
   */
  matchKommun = (name) => {
    if (!name) return undefined;
    const normalize = s => s.toLowerCase().replace(/\s+kommun$/, '').trim();
    const target = normalize(name);
    if (!target) return undefined;
    return this.state.dropDownCities?.find(c => c.value && normalize(c.value) === target);
  }
  
  async componentDidMount() {
    services.getCities().then(data => {
      let dropDownCities = [];
      data.forEach(item => dropDownCities.push({ label: item, value: item }));
      this.setState({ dropDownCities });
    });
    ServiceProjects.getProjectTypes().then(data => {
      this.setState({
        projectTypes: data,
        dropDownProjectTypes: this.getProjectTypeTranslations(data)
      });
    });
    if (this.props.allNextProjects.length == 0) {
      this.props.fetchAllNextProjects();
    }
    if (appState.currentOrganization.hasNextProject && (this.props.allNextCustomers?.length ?? 0) === 0) {
      this.props.fetchAllNextCustomers?.();
    }
  }

  componentWillUnmount() {
    this.form1 = null;
    this.debouncedFetchAddresses?.cancel();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.t !== nextProps.t) {
      this.setState({
        entreprenadOptions: [...appEnum.getEntreprenadDataSource(nextProps.t)],
        dropDownProjectTypes: this.getProjectTypeTranslations(this.state.projectTypes)
      });
    }
    var data = nextProps.currentProject;
    if (data && this.props.t !== nextProps.t) {
      this.setState({
        entreprenad: data.entreprenad
          ? {
              value: data.entreprenad,
              label: appEnum.getEntreprenadValueByKey(data.entreprenad, nextProps.t),
            }
          : undefined,
          projectType: data.projectTypeName
          ? {
              label: this.props.t(`projectType.${data.projectTypeName.replace(/[\s&/]/g, '')}`, data.projectTypeName),
              value: data.projectType,
            }
          : {},
      });
    }
    if (data && this.props.currentProject !== data) {
      var newData = {
        _id: data._id,
        slug: data.slug,
        name: data.name,
        number: data.number,
        customer: data.customer,
        nextCustomerId: data.nextCustomerId ?? null,
        isClientValid: !!data.nextCustomerId,
        province: {
          label: data.province,
          value: data.province,
        },
        address: data.address?.formatted ? data.address : null,
        addressInput: data.address?.formatted || '',
        addressOptions: [],
        entreprenad: data.entreprenad
          ? {
              value: data.entreprenad,
              label: appEnum.getEntreprenadValueByKey(data.entreprenad, nextProps.t),
            }
          : undefined,
        estimatedCost: data.estimatedCost
          ? {
              value: data.estimatedCost,
              label: appEnum.getProjectCostValueByKey(data.estimatedCost),
            }
          : {},
        projectType: data.projectTypeName
          ? {
              label: this.props.t(`projectType.${data.projectTypeName.replace(/[\s&/]/g, '')}`, data.projectTypeName),
              value: data.projectType,
            }
          : {},
        phase: data.phase
          ? {
              label: data.phase,
              value: data.phase,
            }
          : {},
        loaderShow: false,
      };
      this.setState(newData);
      this.reportClientValidity(data.nextCustomerId);
    } else if (this.props.showCount !== nextProps.showCount) {
      this.resetDialog();
    }
  }

  getProjectTypeTranslations(data) {
    return data.map(item => ({
      label: this.props.t(`projectType.${item.name.replace(/[\s&/]/g, '')}`, item.name),
      name: item.name,
      value: item._id
    }));
  }



  resetDialog() {
    this.setState({
      _id: '',
      slug: '',
      name: '',
      number: '',
      responsible: '',
      customer: '',
      nextCustomerId: null,
      province: {},
      address: null,
      addressInput: '',
      addressOptions: [],
      entreprenad: {},
      estimatedCost: {},
      projectType: {},
      phase: { label: '', value: '' },
      parentProject: null,
      loaderShow: false,
    });
    this.form1?.resetForm();
    this.updateClientValidity(false);
  }

  updateClientValidity = (valid) => {
    this.setState({ isClientValid: valid });
    this.props.onCustomerMappingChange?.(valid);
  };

  reportClientValidity = async (nextCustomerId) => {
    if (!nextCustomerId) {
      this.updateClientValidity(false);
      return;
    }
    try {
      const customer = await ServiceNextProject.checkCustomerExists(nextCustomerId);
      this.updateClientValidity(!!(customer && customer.id));
    } catch (e) {
      this.updateClientValidity(false);
    }
  };

  btnCreateProjectClickCallback = () => this.form1.submitForm();

  showError = error => <div className="error-show">{error}</div>;

  renderForm() {
    const { t, allNextProjects, fetchAllNextProjects, allNextCustomers, fetchAllNextCustomers } = this.props;
    const isReadOnlyUser = appState.isReadOnlyAccessRole();
    const requiredText = t('createProject.validation.required', 'Obligatoriskt');
    const { dropDownCities, entreprenadOptions, projectCostOptions, dropDownProjectTypes } = this.state;

    const renderOption = (value) => (
      value ? <CustomAvatar style={{ marginRight: '10px' }} size="24px" src={value.avatarUrl} alt={value.label} /> : null
    );

    
    const currentUserIfNotSuperadmin = this.props.users.find(u => u.value === appState.getUserId()); 
    const responsible = this.props.currentProject?._id ? this.props.currentProject?.responsible : currentUserIfNotSuperadmin;

    return (
      <div id="create-project-modal">
        <Formik
          ref={ el => { this.form1 = el; } }
          initialValues={{
            name: this.state.name || '',
            number: this.state.number || '',
            customer: this.state.customer || '',
            nextCustomerId: this.state.nextCustomerId ?? null,
            province: this.state.province || {},
            address: this.state.address || null,
            projectType: this.state.projectType || {},
            entreprenad: this.state.entreprenad,
            estimatedCost: this.state.estimatedCost || {},
            phase: this.state.phase || {},
            responsible: responsible ? {
              label: responsible.name,
              value: responsible._id,
              ...responsible
            } : undefined,
            user: this.props.currentProject?.user ?? (currentUserIfNotSuperadmin ? [currentUserIfNotSuperadmin] : []),
          }}
          onSubmit={this.submitForm}
          validationSchema={
            Yup.object().shape({
              name: Yup.string().trim().required(requiredText),
              number: Yup.string(),
              customer: Yup.string(),
              province: Yup.object().shape({
                label: Yup.string().required(requiredText),
                value: Yup.string().required(requiredText),
              }),
              entreprenad: Yup.object().shape({
                label: Yup.string().required(requiredText),
                value: Yup.string().required(requiredText),
              }),
              estimatedCost: Yup.object().shape({
                label: Yup.string(),
                value: Yup.string(),
              }),
              projectType: Yup.object().shape({
                label: Yup.string(),
                value: Yup.string(),
              }),
            })
          }
          enableReinitialize={true}
        >
          {({ values, errors, touched, setFieldValue }) => (
            <Form>
              <Field
                className='add-project-field'
                errorMessage={errors.name}
                label={t('createProject.label.name', 'Projektnamn')}
                onChange={(value) => setFieldValue("name", value)}
                value={values.name}
                name="name"
                required
              />
              <Field
                className='add-project-field'
                errorMessage={errors.number}
                label={t('createProject.label.projectNo', 'Projektnummer')}
                onChange={(value) => setFieldValue("number", value)}
                value={values.number}
                name="number"
              />

              {appState.currentOrganization.hasNextProject && appState.currentOrganization.nextProjectAllowExport && (this.props.currentProject?.nextProjectId || this.state.parentProject) ? (
                <div style={{ marginBottom: '10px' }}>
                  <NextEntityPlacementSelector
                    label={t('createProject.label.customer', 'Beställare')}
                    placeholder={t('createProject.placeholder.customer', 'Välj en kund från Next')}
                    isCreatable
                    options={(Array.isArray(allNextCustomers) ? allNextCustomers : []).map(c => ({ label: c.name, value: String(c.id) }))}
                    value={values.customer
                      ? { label: values.customer, value: values.nextCustomerId != null ? String(values.nextCustomerId) : values.customer }
                      : null}
                    onChange={(option) => {
                      if (!option) {
                        setFieldValue('customer', '');
                        setFieldValue('nextCustomerId', null);
                        this.updateClientValidity(false);
                        return;
                      }
                      const matched = (Array.isArray(allNextCustomers) ? allNextCustomers : []).find(c => String(c.id) === option.value);
                      setFieldValue('customer', option.label);
                      setFieldValue('nextCustomerId', matched ? Number(matched.id) : null);
                      this.updateClientValidity(!!matched);
                    }}
                    onRefresh={fetchAllNextCustomers}
                    loading={(allNextCustomers?.length ?? 0) === 0}
                    errorMessage={!this.state.isClientValid ? t('createProject.validation.identifyClient', 'Vänligen identifiera kunden från Next') : undefined}
                  />
                </div>
              ) : (
                <Field
                  className='add-project-field'
                  errorMessage={errors.customer}
                  label={t('createProject.label.customer', 'Beställare')}
                  onChange={(value) => setFieldValue("customer", value)}
                  value={values.customer}
                  name="customer"
                />
              )}

              <Selector
                isCreatable
                isSearchable
                isClearable
                label={t('createProject.label.address', 'Adress')}
                placeholder={t('createProject.placeholder.address', 'Sök adress')}
                options={this.state.addressOptions}
                value={values.address?.formatted
                  ? { label: values.address.formatted, value: values.address.placeId || values.address.formatted }
                  : null}
                onInputChange={this.handleAddressInputChange}
                onChange={option => this.handleAddressChange(option, setFieldValue, values.province)}
                /* Suggestions are already filtered by Google, so short-circuit
                   react-select's client-side filter instead of letting it drop
                   matches that don't literally contain what was typed. */
                getSearchString={() => this.state.addressInput}
                formatCreateLabel={value => value}
                className='add-project-field'
              />

              <Selector
                label={t('createProject.label.region', 'Kommun')}
                placeholder={t('createProject.placeholder.region', 'Vänligen välj')}
                options={dropDownCities?.map(
                  c => ({
                    value: c.value,
                    label: c.value,
                  })
                )}
                value={values.province}
                onChange={v => setFieldValue('province', v, true)}
                className='add-project-field'
                errorMessage={errors.province && touched.province ? errors.province.value : ""}
                required
              />

              <Selector
                label={t('createProject.label.entreprenad', 'Entreprenadform')}
                placeholder={t('createProject.placeholder.entreprenad', 'Vänligen välj')}
                options={entreprenadOptions?.map(
                  eo => ({
                    value: eo.key,
                    label: eo.value,
                  })
                )}
                value={values.entreprenad}
                onChange={v => setFieldValue('entreprenad', v, true)}
                className='add-project-field'
                errorMessage={errors.entreprenad && touched.entreprenad ? errors.entreprenad.value : ""}
                required
              />

              <Selector
                label={t('createProject.label.estCost', 'Estimerad projektkostnad i MSEK')}
                placeholder={t('createProject.placeholder.estCost', 'Vänligen välj')}
                options={projectCostOptions?.map(
                  pco => ({
                    value: pco.key,
                    label: pco.value,
                  })
                )}
                value={values.estimatedCost}
                onChange={v => setFieldValue('estimatedCost', v, true)}
                className='add-project-field'
                errorMessage={errors.estimatedCost && touched.estimatedCost ? errors.estimatedCost.value : ""}
              />

              <Selector
                label={t('createProject.label.type', 'Projekttyp')}
                placeholder={t('createProject.placeholder.type', 'Vänligen välj')}
                name="projectType"
                options={dropDownProjectTypes?.map(
                  c => ({
                    value: c.value,
                    name: c.name,
                    label: c.label,
                  })
                )}
                value={values.projectType}
                onChange={v => setFieldValue('projectType', v, true)}
                className='add-project-field'
                errorMessage={errors.projectType && touched.projectType ? errors.projectType.value : ""}
              />
              <Selector
                label={t('createProject.label.phase', 'Skede')}
                placeholder={t('createProject.placeholder.phase', 'Vänligen välj')}
                name="phase"
                options={[
                  { label: t('projectPhase.AnbudKalkyl', 'Anbud/Kalkyl'), value: 'Anbud/Kalkyl' },
                  { label: t('projectPhase.InköpProduktion', 'Inköp/Produktion'), value: 'Inköp/Produktion' }
                ]}
                value={values.phase}
                onChange={v => setFieldValue('phase', v, true)}
                className="add-project-field"
                errorMessage={errors.phase && touched.phase ? errors.phase.value : ''}
              />

              <UserSelector
                label={t('createProject.label.responsible', 'Medarbetare')}
                name="responsible"
                options={this.props.users}
                value={values.responsible ? {
                  label: values.responsible.name, 
                  value: values.responsible._id, 
                  ...values.responsible
                } : null}
                onChange={responsible => {
                  const transformedResponsible = responsible ? {
                    ...responsible,
                    name: responsible.label,
                    _id: responsible.value
                  } : null;
                  setFieldValue('responsible', transformedResponsible);
                }}
                className="add-project-field"
              />
              
              <UserSelector
                isMulti={true}
                label={t('createProject.headline.employees', 'Medarbetare')}
                value={values.user?.map(u => {
                  return ({ label: u.name, value: u._id, ...u });
                }) || []}
                onChange={users => {
                  const transformedUsers = users.map(u => ({
                    ...u,
                    name: u.label,
                    _id: u.value
                  }));
                  setFieldValue('user', transformedUsers);
                }}
                options={this.props.users}
                className="add-project-field"
              />
              {appState.currentOrganization.hasNextProject && appState.currentOrganization.nextProjectAllowExport && !this.props.currentProject?.nextProjectId && (
                <NextEntityPlacementSelector
                  value={this.state.parentProject}
                  onChange={v => {
                    this.setState({ parentProject: v });
                    this.props.onParentProjectChange?.(v);
                  }}
                  options={allNextProjects.map(p => ({ label: `${p.projectno} ${p.name}`, value: String(p.id) }))}
                  onRefresh={fetchAllNextProjects}
                  loading={allNextProjects.length === 0}
                  label={t('exportNextProjectModal.parentProjectLabel', 'Placering')}
                  placeholder={t('exportNextProjectModal.selectParentProject', 'Välj överordnat projekt')}
                />
              )}
            </Form>
          )}
        </Formik>
      </div>
    );
  }


  submitForm = async (values, { setSubmitting }) => {
    const {t} = this.props;
    try {
      setSubmitting(true);
      this.setState({ loaderShow: true });

      // A Places details lookup for the address the user just picked may still
      // be resolving. Saving without waiting would store only the unresolved
      // {formatted, placeId} and lose lat/lng and the components permanently.
      const resolvedAddress = this.addressDetailsPromise
        ? await this.addressDetailsPromise
        : undefined;
      const address = resolvedAddress ?? values.address;

      this.setState({ ...values, address });
      let newData = {
        _id: this.state._id,
        slug: this.state.slug,
        name: values.name?.trim(),
        responsible: values.responsible?.value || null,
        entreprenad: values.entreprenad?.value,
        user: (values.user || []).map(u => u._id),
      };
      
      if (values.province) newData.province = values.province.value;
      // Sent unconditionally, unlike the guarded fields above: PUT /project/:slug
      // only overwrites the address when the key is present, so omitting it on a
      // cleared field would silently keep the old value.
      newData.address = address?.formatted ? address : null;
      if (values.number) newData.number = values.number;
      if (values.customer) newData.customer = values.customer;
      newData.nextCustomerId = values.nextCustomerId ?? null;
      if (values.projectType) {
        newData.projectType = values.projectType.value;
        newData.projectTypeName = values.projectType.name;
      }
      if (values.estimatedCost) newData.estimatedCost = values.estimatedCost?.value;
      if (values.phase?.value) newData.phase = values.phase.value;
      if (values.responsible?.value) { 
        newData.responsible = values.responsible?.value;
      }else{
          newData.responsible = appState.getLoggedInUserDetail().id;
      }
      let response;
      if (this.state._id) {
        response = await ServiceProjects.updateProject(newData);
        var newProject = { ...this.props.currentProject };
        newProject.slug = response.slug;
        newProject.name = response.name;
        newProject.number = response.number;
        newProject.customer = response.customer;
        newProject.nextCustomerId = response.nextCustomerId;
        newProject.province = response.province;
        newProject.address = response.address;
        newProject.projectType = newData.projectType;
        newProject.projectTypeName = newData.projectTypeName;
        newProject.estimatedCost = response.estimatedCost;
        newProject.entreprenad = response.entreprenad;
        newProject.phase = response.phase;
        newProject.responsible = values.responsible || null;
        newProject.user = values.user || [];
        response = newProject;
        ServiceProjects.replaceDataItem(response);
        response.isEdit = true;
        NotificationManager.success(t('createProject.notification.success.updated','Projektet har uppdaterats'))
      } else {
        response = await ServiceProjects.addProject(newData);
        response.user = values.user || [];
        response.responsible = values.responsible || null;
        ServiceProjects.add(response);
        response.isEdit = false;
        NotificationManager.success(t('createProject.notification.success.created', 'Projektet har skapats'))
      }

      // Update project members in one call
      const projectId = response._id || this.state._id;
      const userIds = (values.user || []).map(u => u._id || u.value);
      await ServiceUser.updateProjectMembers(projectId, userIds);

      if (this.props.onProjectUpdateCreated) {
        this.props.onProjectUpdateCreated(this, response);
      }
    } catch (e) {
      console.error('error', e);
      NotificationManager.error(
        t(
          `notification.error.description.${e?.response?.data?.error || 'unexpected'}`,
          'Oväntat fel ({{error}})',
          { error: e?.response?.data?.error || 'unexpected' }
        ),
        t('createProject.notification.error.title', 'Misslyckat')
      );
    } finally {
      this.setState({ loaderShow: false });
      setSubmitting(false);
    }
  };

  onClose = () => this.props?.onClose();

  render() {
    return (
      <div className="app-add-supplier app-create-project" ref={el => (this.element = el)}>
        <div className="page-paddings">
          <BtGrid>
            <BtRow>
              <BtColumn className="colum-left pp-col-left">
                <div className="content-left">{this.renderForm()}</div>
              </BtColumn>
            </BtRow>
          </BtGrid>
        </div>
      </div>
    );
  }
}

export default withTranslation('projects', { withRef: true })(AppCreateProject);
